import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  User,
  AuthError,
  getAuth,
  onAuthStateChanged,
} from "firebase/auth";
import {
     getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { auth} from "./firebase";
import { app } from "../lib/firebase";
import CreatePhotURL from "../UserProfile/CreatePhotoURL";
const db = getFirestore(app);

export interface UserData {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserDataTemp {
  userUID: string;
  userEmail: string;
  userProfileName: string;
  userPhotoName: string;
  userConnections?: Array<string>;
  createdAt: Date;
  updatedAt: Date;
}

// Sign up new user
export const signUpUser = async (
  email: string,
  password: string,
  displayName: string
): Promise<{ user: User; userData: UserData } | { error: string }> => {
  try {
    // Create user with email and password
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Update the user's display name
    await updateProfile(user, {
      displayName: displayName,
    });

    // Create user document in Firestore
    const userData: UserData = {
      uid: user.uid,
      email: user.email!,
      displayName: displayName,
      photoURL: user.photoURL || "",
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Create user document in Firestore
    const userDataTemp: UserDataTemp = {
      userUID: user.uid,
      userEmail: user.email!,
      userProfileName: displayName,
      userPhotoName: user.photoURL || "",
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(doc(db, "userProfiles", user.uid), userData);

    // Store user info in localStorage for immediate use
    localStorage.setItem("userUID", user.uid);
    localStorage.setItem("userName", displayName);
    localStorage.setItem("userEmail", user.email!);

    return { user, userData };
  } catch (error) {
    const authError = error as AuthError;
    let errorMessage = "An error occurred during sign up";

    switch (authError.code) {
      case "auth/email-already-in-use":
        errorMessage =
          "This email is already registered. Please use a different email or sign in.";
        break;
      case "auth/invalid-email":
        errorMessage = "Please enter a valid email address.";
        break;
      case "auth/weak-password":
        errorMessage = "Password should be at least 6 characters long.";
        break;
      case "auth/operation-not-allowed":
        errorMessage =
          "Email/password accounts are not enabled. Please contact support.";
        break;
      default:
        errorMessage = authError.message;
    }

    return { error: errorMessage };
  }
};

// Sign in existing user
export const signInUser = async (
  email: string,
  password: string
): Promise<{ user: User; userData: UserData } | { error: string }> => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, "userProfiles", user.uid));

    let userData: UserData;
    if (userDoc.exists()) {
      userData = userDoc.data() as UserData;
      // Update last login time
      userData.updatedAt = new Date();
      await setDoc(doc(db, "userProfiles", user.uid), userData);
    } else {
      // Create user document if it doesn't exist (for existing Firebase Auth users)
      userData = {
        uid: user.uid,
        email: user.email!,
        displayName: user.displayName || "User",
        photoURL: user.photoURL || "",
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      await setDoc(doc(db, "userProfiles", user.uid), userData);
    }

    // Store user info in localStorage
    localStorage.setItem("userUID", user.uid);
    localStorage.setItem("userName", userData.displayName);
    localStorage.setItem("userEmail", user.email!);

    return { user, userData };
  } catch (error) {
    const authError = error as AuthError;
    let errorMessage = "An error occurred during sign in";

    switch (authError.code) {
      case "auth/user-not-found":
        errorMessage =
          "No account found with this email. Please check your email or sign up.";
        break;
      case "auth/wrong-password":
        errorMessage = "Incorrect password. Please try again.";
        break;
      case "auth/invalid-email":
        errorMessage = "Please enter a valid email address.";
        break;
      case "auth/user-disabled":
        errorMessage =
          "This account has been disabled. Please contact support.";
        break;
      case "auth/too-many-requests":
        errorMessage = "Too many failed attempts. Please try again later.";
        break;
      default:
        errorMessage = authError.message;
    }

    return { error: errorMessage };
  }
};

// Sign out user
export const signOutUser = async (): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    await signOut(auth);

    // Clear localStorage
    localStorage.removeItem("userUID");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("myPicture");

    return { success: true };
  } catch (error) {
    const authError = error as AuthError;
    return { success: false, error: authError.message };
  }
};

// Get current user data
export const getCurrentUserData = async (): Promise<UserData | null> => {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    const userDoc = await getDoc(doc(db, "userProfiles", user.uid));
    if (userDoc.exists()) {
      return userDoc.data() as UserData;
    }
    return null;
  } catch (error) {
    console.error("Error fetching user data:", error);
    return null;
  }
};

// Validate email format
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate password strength
export const isValidPassword = (
  password: string
): { isValid: boolean; message: string } => {
  if (password.length < 6) {
    return {
      isValid: false,
      message: "Password must be at least 6 characters long",
    };
  }
  if (password.length > 128) {
    return {
      isValid: false,
      message: "Password must be less than 128 characters",
    };
  }
  return { isValid: true, message: "Password is valid" };
};

// Fetch all users from Firebase (excluding current user)
export const getAllUsers = async (): Promise<UserData[]> => {
  try {
    const auth = getAuth();
    const potentialUserObject = {
  uid: '',
  email: '',
  displayName: '',
};
    let currentUser = potentialUserObject as User;
onAuthStateChanged(auth, (user) => {
  if (user) {
    currentUser = user;
    // User is signed in.
    // You can now access user properties like user.uid, user.displayName, etc.
  } else {
    // User is signed out.
  }
});

    //const currentUser = auth.currentUser;
    console.log("at the beginning of getAllUsers and currentUser = ", currentUser);
    const usersCollection = collection(db, "userProfiles");

    const users: UserData[] = [];
    let i = 0;
    let connections = [];

    let usersSnapshot = await getDocs(usersCollection);
    usersSnapshot.forEach((doc) => {
      let userDataTemp = doc.data() as UserDataTemp;
      console.log("getAllUsers and userDataTemp = ", userDataTemp);
      if (currentUser  && userDataTemp.userUID === currentUser.uid) {
        for (let j = 0; j < userDataTemp.userConnections.length; j++ ){
           connections[j] = userDataTemp.userConnections[j]; 
          }
        }
    });
    console.log("getAllUsers and currentUser = ", currentUser);
    

    let usersSnapshot2 = await getDocs(usersCollection);
    usersSnapshot2.forEach((doc) => {
      let userDataTemp = doc.data() as UserDataTemp;
      for (let k = 0; k < connections.length; k++){
        if (userDataTemp.userEmail === connections[i]) {
          users[i] = {} as UserData;
          users[i].uid = userDataTemp.userUID;
          users[i].email = userDataTemp.userEmail;
          users[i].displayName = userDataTemp.userProfileName;
          users[i].photoURL = userDataTemp.userPhotoName;
          users[i].createdAt = userDataTemp.createdAt;
          users[i].updatedAt = userDataTemp.updatedAt;
          users[i].photoURL = CreatePhotURL(users[i].photoURL);
          i++;
        }
      }

    });
    console.log("getAllUsers and users = ", users);
    return users;
  } 
  catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

// Search users by display name or email
export const searchUsers = async (searchTerm: string): Promise<UserData[]> => {
  try {
    const currentUser = auth.currentUser;
    const usersCollection = collection(db, "userProfiles");
    const usersSnapshot = await getDocs(usersCollection);

    const users: UserData[] = [];
    const searchLower = searchTerm.toLowerCase();

    usersSnapshot.forEach((doc) => {
      const userData = doc.data() as UserData;
      // Exclude current user and filter by search term
      if (currentUser && userData.uid !== currentUser.uid) {
        const nameMatch = userData.displayName
          .toLowerCase()
          .includes(searchLower);
        const emailMatch = userData.email.toLowerCase().includes(searchLower);

        if (nameMatch || emailMatch) {
          users.push(userData);
        }
      }
    });

    return users;
  } catch (error) {
    console.error("Error searching users:", error);
    return [];
  }
};